import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useMobile } from './MobileContext';

const SidebarContext = createContext();

export const SidebarProvider = ({ children }) => {
    const { isMobile } = useMobile();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem('sidebarCollapsed');
        // On mobile, always start collapsed
        if (isMobile) {
            return true;
        }
        return saved ? JSON.parse(saved) : false;
    });
    const [page, setPage] = useState(null);
    const sidebarRef = useRef(null);

    const handleClickOutside = useCallback(event => {
        if (isMobile
            && !isSidebarCollapsed
            && sidebarRef.current
            && !sidebarRef.current.contains(event.target)
        ) {
            setIsSidebarCollapsed(true);
        }
    }, [isMobile, isSidebarCollapsed]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarCollapsed(prev => {
            const newState = !prev;
            // Only save state to localStorage on desktop
            if (!isMobile) {
                localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
            }
            return newState;
        });
    }, [isMobile]);

    // Function to close sidebar (for use in navigation)
    const closeSidebar = useCallback(() => {
        if (isMobile && !isSidebarCollapsed) {
            setIsSidebarCollapsed(true);
        }
    }, [isMobile, isSidebarCollapsed]);

    return (
        <SidebarContext.Provider value={{ 
            isSidebarCollapsed,
            setIsSidebarCollapsed,
            toggleSidebar, 
            page, 
            setPage, 
            closeSidebar,
            sidebarRef,
        }}>
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (context === undefined) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};
